import { useEffect, useMemo, useRef } from 'react';
import { getShopRegion } from '@oec-seller/sdk/hooks';

const CSP_APP_ID = '298722';

type TrackEvent = {
  view: (eventId: unknown, params?: Record<string, unknown>) => void;
  click: (eventId: unknown, params?: Record<string, unknown>) => void;
  action: (eventId: unknown, params?: Record<string, unknown>) => void;
  impression: (eventId: unknown, params?: Record<string, unknown>) => void;
};

type TrackMethod = keyof TrackEvent;

type QueuedCall = {
  method: TrackMethod;
  args: unknown[];
};

export const useTeaReport = (): TrackEvent => {
  const trackerRef = useRef<any>(null);
  const queueRef = useRef<QueuedCall[]>([]);

  const shopRegion = useMemo((): string => {
    const { search } = window.location;
    const matchedRegion = (search || '').match(/shop_region=(\w+)/) || (search || '').match(/region=(\w+)/);
    return matchedRegion ? matchedRegion[1] : getShopRegion();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const requestIdleCallback = (window as any).requestIdleCallback as
      | ((cb: () => void, options?: { timeout: number }) => number)
      | undefined;
    const cancelIdleCallback = (window as any).cancelIdleCallback as ((id: number) => void) | undefined;

    const flushQueue = (): void => {
      const tracker = trackerRef.current;
      if (!tracker) {
        return;
      }

      queueRef.current.forEach(({ method, args }) => {
        try {
          tracker[method]?.(...args);
        } catch (err) {
          console.error('Tracker method failed', err);
        }
      });

      queueRef.current = [];
    };

    const loadTracker = async (): Promise<void> => {
      try {
        const { EventTracker } = await import('@ies/csp_event_tracking');
        if (cancelled) {
          return;
        }

        trackerRef.current = new EventTracker(
          CSP_APP_ID,
          {
            platform: 'pc',
            region: shopRegion
          },
          () => {
            // ignore
          }
        );

        flushQueue();
      } catch (err) {
        console.error('Failed to load EventTracker', err);
      }
    };

    const schedule = (): void => {
      if (cancelled) {
        return;
      }
      void loadTracker();
    };

    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(schedule, { timeout: 2000 });
      const cleanup = (): void => {
        cancelled = true;
        if (typeof cancelIdleCallback === 'function') {
          cancelIdleCallback(id);
        }
      };
      return cleanup;
    }

    const timeoutId = window.setTimeout(schedule, 2000);
    const cleanup = (): void => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
    return cleanup;
  }, [shopRegion]);

  return useMemo((): TrackEvent => {
    const call =
      (method: TrackMethod): ((...args: unknown[]) => void) =>
      (...args: unknown[]): void => {
        const tracker = trackerRef.current;
        if (tracker) {
          try {
            tracker[method]?.(...args);
          } catch (err) {
            console.error('Tracker method failed', err);
          }
          return;
        }

        queueRef.current.push({ method, args });
      };

    return {
      view: call('view'),
      click: call('click'),
      action: call('action'),
      impression: call('impression')
    };
  }, []);
};
